import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'ai-wins-theme';

// Preference is what the user chose ('system' | 'light' | 'dark').
// Effective is what's actually applied ('light' | 'dark') after resolving 'system'.
const ThemeContext = createContext({
  pref: 'system',
  effective: 'light',
  setPref: () => {},
  toggle: () => {},
});

function prefersDark() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(pref) {
  return pref === 'system' ? (prefersDark() ? 'dark' : 'light') : pref;
}

function applyTheme(effective) {
  document.documentElement.setAttribute('data-theme', effective);
}

export function ThemeProvider({ children }) {
  const [pref, setPrefState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'system'; } catch { return 'system'; }
  });
  const [effective, setEffective] = useState(() => resolve(pref));

  // Apply the resolved theme to <html> and persist the preference.
  useEffect(() => {
    const eff = resolve(pref);
    setEffective(eff);
    applyTheme(eff);
    try { localStorage.setItem(STORAGE_KEY, pref); } catch { /* ignore */ }
  }, [pref]);

  // While following the system, track OS light/dark changes live.
  useEffect(() => {
    if (pref !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const eff = mq.matches ? 'dark' : 'light';
      setEffective(eff);
      applyTheme(eff);
    };
    // addEventListener is newer; addListener kept for older Safari.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [pref]);

  const setPref = useCallback((p) => setPrefState(p), []);

  // Single click flips to the opposite of what's currently shown. If the new
  // choice matches the OS setting, resume following the system (so macOS auto
  // day/night keeps working); otherwise pin the explicit choice.
  const toggle = useCallback(() => {
    setPrefState((p) => {
      const sys = prefersDark() ? 'dark' : 'light';
      const currentEffective = p === 'system' ? sys : p;
      const next = currentEffective === 'dark' ? 'light' : 'dark';
      return next === sys ? 'system' : next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ pref, effective, setPref, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
